import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Camera,
  Aperture,
  Sun,
  Layers,
  Wand2,
  ArrowRight,
  BookOpen,
  CheckCircle2,
  Clock3,
} from "lucide-react";
import heroCamera from "@/assets/hero-camera.jpg";

const TITLE = "Cómo aprender fotografía desde cero: Guía completa";
const DESCRIPTION =
  "Guía práctica para aprender fotografía desde cero: elegir cámara, entender la exposición, dominar el enfoque y aplicar las reglas básicas de composición.";
const URL = "https://fotopasion.lovable.app/guia-principiantes";

const steps = [
  {
    id: "camara",
    icon: Camera,
    title: "Elige la cámara adecuada (y no gastes de más)",
    practice:
      "Haz diez fotos con el equipo que ya tienes y anota qué control técnico echas en falta antes de comprar nada.",
    body: [
      "No necesitas una cámara profesional para aprender fotografía. Necesitas una cámara que te permita controlar la exposición: cualquier réflex, sin espejo o incluso un móvil con modo manual sirve perfectamente para empezar.",
      "Si compras tu primera cámara, prioriza un cuerpo sencillo con un objetivo luminoso (por ejemplo un 50 mm f/1.8) antes que un cuerpo caro con un objetivo mediocre. El objetivo influye mucho más en la calidad final de la imagen que los megapíxeles del sensor.",
      "Si vas a empezar con el teléfono, activa el modo profesional o instala una app que te deje ajustar ISO, velocidad y enfoque manualmente. Aprenderás exactamente los mismos conceptos y podrás practicar todos los días sin cargar peso.",
    ],
  },
  {
    id: "exposicion",
    icon: Aperture,
    title: "Entiende el triángulo de exposición",
    practice:
      "Fotografía el mismo objeto variando primero la apertura y después la velocidad. Compara el fondo, el movimiento y la luminosidad.",
    body: [
      "La exposición es el equilibrio entre tres ajustes: apertura de diafragma, velocidad de obturación e ISO. Cambiar uno obliga a compensar con otro, y esa relación es la base de toda la técnica fotográfica.",
      "La apertura (f/1.8, f/8, f/16) controla cuánta luz entra y cuánta profundidad de campo obtienes: números bajos desenfocan el fondo, números altos mantienen todo nítido.",
      "La velocidad de obturación congela o difumina el movimiento: 1/1000 s congela a un ciclista, 1/15 s convierte el agua de un río en seda. La ISO amplifica la señal del sensor cuando falta luz, a costa de añadir ruido.",
      "Ejercicio recomendado: fotografía el mismo objeto en modo manual variando solo la apertura, luego solo la velocidad, y compara los resultados. Es la forma más rápida de interiorizar el triángulo.",
    ],
  },
  {
    id: "luz",
    icon: Sun,
    title: "Aprende a leer la luz",
    practice:
      "Fotografía una misma escena con luz frontal, lateral y a contraluz. Observa cómo cambian el volumen, las sombras y el ambiente.",
    body: [
      "La luz es la materia prima de la fotografía. Antes de disparar, observa de dónde viene, si es dura (sombras marcadas, sol del mediodía) o suave (día nublado, ventana grande) y qué color tiene.",
      "La luz lateral da volumen y textura; la luz frontal aplana; el contraluz crea siluetas y recortes. La hora dorada, justo después del amanecer y antes del atardecer, es la más agradecida para empezar.",
      "Practicar con luz natural durante los primeros meses te enseñará más que comprar un equipo de flash que aún no sabrás dirigir.",
    ],
  },
  {
    id: "composicion",
    icon: Layers,
    title: "Domina las reglas básicas de composición",
    practice:
      "Elige una sola regla de composición y crea cinco imágenes distintas aplicándola de forma consciente.",
    body: [
      "Regla de los tercios: divide el encuadre en una cuadrícula 3×3 y sitúa los elementos importantes sobre las líneas o sus intersecciones. Es el punto de partida más fiable.",
      "Líneas guía: caminos, barandillas o sombras que conducen la mirada hacia el sujeto. Encuadre natural: puertas, ramas o arcos que enmarcan la escena.",
      "Espacio negativo: deja aire alrededor del sujeto para que respire. Punto de vista: agáchate, sube, acércate — cambiar la altura de la cámara suele mejorar más una foto que cualquier ajuste técnico.",
      "Las reglas se aprenden para poder romperlas con criterio. Primero interiorízalas, después experimenta.",
    ],
  },
  {
    id: "edicion",
    icon: Wand2,
    title: "Revela y edita tus fotos",
    practice:
      "Edita una fotografía ajustando únicamente exposición, balance de blancos, contraste y recorte. Compara después el antes y el después.",
    body: [
      "Dispara en RAW en cuanto te sientas cómodo: conserva mucha más información para recuperar luces y sombras en la edición.",
      "Un flujo de edición básico consiste en corregir exposición y balance de blancos, ajustar contraste y curvas, recortar para reforzar la composición y aplicar enfoque al final.",
      "Herramientas como Luminar NEO simplifican mucho este proceso con ajustes asistidos por inteligencia artificial, pero el criterio visual siempre lo pones tú.",
    ],
  },
];

const plan = [
  {
    period: "Semanas 1–2",
    title: "Controla el modo manual",
    detail: "Haz una foto diaria variando apertura y velocidad.",
  },
  {
    period: "Semanas 3–4",
    title: "Entrena la composición",
    detail: "Trabaja un tema por día: líneas, tercios, marcos o simetría.",
  },
  {
    period: "Semanas 5–6",
    title: "Observa la luz",
    detail: "Fotografía el mismo lugar a distintas horas del día.",
  },
  {
    period: "Semanas 7–8",
    title: "Construye tu flujo de edición",
    detail: "Revela en RAW tus 20 mejores tomas y compáralas con el original.",
  },
];

const faqs = [
  {
    q: "¿Cuánto se tarda en aprender fotografía desde cero?",
    a: "Con práctica constante, en dos o tres meses puedes dominar la exposición manual y la composición básica. La evolución del estilo personal es un proceso continuo.",
  },
  {
    q: "¿Puedo aprender fotografía solo con el móvil?",
    a: "Sí. Los conceptos de luz, encuadre y composición son idénticos, y muchos móviles permiten control manual de ISO, velocidad y enfoque.",
  },
  {
    q: "¿Qué es mejor para empezar: un curso o aprender por mi cuenta?",
    a: "Un curso estructurado te ahorra meses de prueba y error porque ordena los conceptos y te da ejercicios progresivos. Después, la práctica diaria hace el resto.",
  },
];

export const Route = createFileRoute("/guia-principiantes")({
  head: () => ({
    meta: [
      { title: "Cómo aprender fotografía desde cero · Guía completa" },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://fotopasion.lovable.app/guia-principiantes-og.jpg" },
      {
        name: "twitter:image",
        content: "https://fotopasion.lovable.app/guia-principiantes-og.jpg",
      },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: TITLE,
          description: DESCRIPTION,
          inLanguage: "es-ES",
          mainEntityOfPage: URL,
          author: { "@type": "Organization", name: "FotoPasión" },
          publisher: {
            "@type": "Organization",
            name: "FotoPasión",
            url: "https://fotopasion.lovable.app",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Inicio",
              item: "https://fotopasion.lovable.app/",
            },
            { "@type": "ListItem", position: 2, name: "Guía para principiantes", item: URL },
          ],
        }),
      },
    ],
  }),
  component: GuiaPrincipiantes,
});

function GuiaPrincipiantes() {
  return (
    <div className="min-h-screen bg-background font-sans text-foreground">
      <header className="sticky top-0 z-50 border-b border-border/60 bg-background/80 backdrop-blur-lg">
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3 md:px-8">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="FotoPasión" width={96} height={96} className="h-12 w-auto" />
            <span className="hidden text-lg font-bold text-foreground sm:inline">FotoPasión</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/blog"
              className="text-sm font-medium text-muted-foreground transition hover:text-foreground"
            >
              Blog
            </Link>
            <Link
              to="/"
              hash="cursos"
              className="inline-flex items-center gap-2 rounded-full bg-cta px-5 py-2.5 text-sm font-semibold text-cta-foreground shadow-[var(--shadow-soft)] transition hover:brightness-95"
            >
              Ver cursos <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </nav>
      </header>

      <main>
        <section
          className="relative overflow-hidden"
          style={{ background: "var(--gradient-hero)" }}
        >
          <div className="mx-auto grid max-w-7xl items-center gap-10 px-6 py-14 md:px-8 md:py-20 lg:grid-cols-[1.05fr_0.95fr]">
            <div>
              <nav aria-label="Ruta de navegación" className="text-sm text-muted-foreground">
                <Link to="/" className="font-medium text-primary hover:underline">
                  Inicio
                </Link>
                <span className="mx-2" aria-hidden="true">
                  /
                </span>
                <span>Guía para principiantes</span>
              </nav>
              <span className="mt-7 inline-flex items-center gap-2 rounded-full bg-white/75 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary backdrop-blur">
                <BookOpen className="h-4 w-4" /> Guía gratuita
              </span>
              <h1 className="mt-4 max-w-3xl text-balance font-display text-4xl font-bold leading-[1.08] tracking-tight md:text-5xl lg:text-6xl">
                Aprende fotografía desde cero{" "}
                <span className="italic text-primary">paso a paso</span>
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground md:text-xl">
                Una ruta clara y práctica para elegir tu equipo, controlar la exposición, comprender
                la luz, componer mejores imágenes y empezar a editar con criterio.
              </p>
              <div className="mt-7 flex flex-wrap gap-3 text-sm font-medium">
                <span className="inline-flex items-center gap-2 rounded-full bg-card px-4 py-2 shadow-sm">
                  <Clock3 className="h-4 w-4 text-primary" /> 15 minutos de lectura
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-card px-4 py-2 shadow-sm">
                  <CheckCircle2 className="h-4 w-4 text-primary" /> 5 pasos prácticos
                </span>
              </div>
            </div>
            <div className="relative">
              <div className="overflow-hidden rounded-[2rem] border-8 border-white/70 shadow-[var(--shadow-soft)]">
                <img
                  src={heroCamera}
                  alt="Persona aprendiendo fotografía con una cámara"
                  width={1920}
                  height={1080}
                  className="aspect-[4/3] w-full object-cover"
                />
              </div>
              <div className="absolute -bottom-5 left-5 right-5 rounded-2xl bg-secondary p-4 text-secondary-foreground shadow-[var(--shadow-soft)] sm:left-auto sm:max-w-xs">
                <p className="text-sm font-semibold text-cta">El mejor equipo para empezar</p>
                <p className="mt-1 text-sm text-secondary-foreground/75">
                  Es el que ya tienes y puedes usar hoy.
                </p>
              </div>
            </div>
          </div>
        </section>

        <article className="mx-auto grid max-w-7xl items-start gap-10 px-6 py-16 md:px-8 md:py-24 lg:grid-cols-[280px_minmax(0,1fr)] lg:gap-16">
          <aside className="rounded-3xl border border-border bg-card p-6 lg:sticky lg:top-28">
            <p className="text-sm font-semibold uppercase tracking-wider text-primary">
              En esta guía
            </p>
            <nav className="mt-5" aria-label="Contenido de la guía">
              <ol className="space-y-1">
                {steps.map((step, index) => (
                  <li key={step.id}>
                    <a
                      href={`#${step.id}`}
                      className="flex gap-3 rounded-xl px-3 py-2.5 text-sm leading-snug text-muted-foreground transition hover:bg-secondary/10 hover:text-foreground"
                    >
                      <span className="font-semibold text-primary">{index + 1}</span>
                      <span>{step.title}</span>
                    </a>
                  </li>
                ))}
              </ol>
              <div className="my-4 border-t border-border" />
              <a
                href="#plan"
                className="block rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground transition hover:bg-secondary/10 hover:text-foreground"
              >
                Plan de 8 semanas
              </a>
              <a
                href="#preguntas"
                className="block rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground transition hover:bg-secondary/10 hover:text-foreground"
              >
                Preguntas frecuentes
              </a>
            </nav>
          </aside>

          <div className="min-w-0">
            <div className="rounded-3xl border border-primary/20 bg-primary/5 p-6 md:p-8">
              <h2 className="font-display text-2xl font-bold">Antes de empezar</h2>
              <p className="mt-3 text-[1.0625rem] leading-7 text-muted-foreground">
                No intentes dominarlo todo a la vez. Sigue los pasos en orden, realiza el ejercicio
                de cada bloque y revisa tus propias fotografías: aprenderás más comparando
                resultados que memorizando ajustes.
              </p>
            </div>

            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <section
                  id={step.id}
                  key={step.id}
                  className="scroll-mt-28 border-b border-border py-14 first:pt-16 md:py-16"
                >
                  <div className="flex items-start gap-4">
                    <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-secondary text-cta shadow-sm">
                      <Icon className="h-7 w-7" />
                    </span>
                    <div>
                      <span className="text-sm font-semibold uppercase tracking-wider text-primary">
                        Paso {index + 1}
                      </span>
                      <h2 className="mt-1 text-balance font-display text-3xl font-bold leading-snug md:text-4xl">
                        {step.title}
                      </h2>
                    </div>
                  </div>
                  <div className="mt-7 space-y-5 text-[1.0625rem] leading-8 text-muted-foreground">
                    {step.body.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                  </div>
                  <div className="mt-8 rounded-2xl border-l-4 border-coral bg-coral/10 p-5">
                    <p className="text-sm font-semibold uppercase tracking-wider text-coral-foreground">
                      Ponlo en práctica
                    </p>
                    <p className="mt-2 leading-7 text-foreground/80">{step.practice}</p>
                  </div>
                </section>
              );
            })}

            <section id="plan" className="scroll-mt-28 py-14 md:py-16">
              <span className="text-sm font-semibold uppercase tracking-wider text-primary">
                Tu hoja de ruta
              </span>
              <h2 className="mt-2 text-balance font-display text-3xl font-bold md:text-4xl">
                Un plan de práctica de 8 semanas
              </h2>
              <p className="mt-4 max-w-2xl text-[1.0625rem] leading-7 text-muted-foreground">
                Avanza por bloques de dos semanas y céntrate en una sola habilidad cada vez.
              </p>
              <ol className="mt-8 grid gap-4 sm:grid-cols-2">
                {plan.map((item, index) => (
                  <li
                    key={item.period}
                    className="rounded-3xl border border-border bg-card p-6 shadow-sm"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <span className="rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-cta">
                        {item.period}
                      </span>
                      <span className="font-display text-3xl font-bold text-coral/70">
                        {index + 1}
                      </span>
                    </div>
                    <h3 className="mt-5 text-lg font-semibold tracking-tight">{item.title}</h3>
                    <p className="mt-2 leading-7 text-muted-foreground">{item.detail}</p>
                  </li>
                ))}
              </ol>
              <div className="mt-5 rounded-2xl bg-secondary p-6 text-secondary-foreground">
                <p className="font-semibold text-cta">¿Y después?</p>
                <p className="mt-2 leading-7 text-secondary-foreground/75">
                  Elige una especialidad —retrato, producto, calle o paisaje— y profundiza con un
                  proyecto propio.
                </p>
              </div>
            </section>

            <section id="preguntas" className="scroll-mt-28 border-t border-border py-14 md:py-16">
              <span className="text-sm font-semibold uppercase tracking-wider text-primary">
                Resolvemos tus dudas
              </span>
              <h2 className="mt-2 font-display text-3xl font-bold md:text-4xl">
                Preguntas frecuentes
              </h2>
              <div className="mt-7 space-y-4">
                {faqs.map((faq) => (
                  <details
                    key={faq.q}
                    className="group rounded-2xl border border-border bg-card p-6 transition hover:border-primary/40"
                  >
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-sans text-lg font-semibold tracking-tight">
                      {faq.q}
                      <span
                        aria-hidden="true"
                        className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-secondary text-cta transition group-open:rotate-45"
                      >
                        +
                      </span>
                    </summary>
                    <p className="mt-4 pr-10 text-[1.0625rem] leading-7 text-muted-foreground">
                      {faq.a}
                    </p>
                  </details>
                ))}
              </div>
            </section>

            <section className="rounded-[2rem] bg-secondary p-8 text-center text-secondary-foreground shadow-[var(--shadow-soft)] md:p-12">
              <span className="text-sm font-semibold uppercase tracking-wider text-cta">
                Siguiente paso
              </span>
              <h2 className="mx-auto mt-2 max-w-2xl text-balance font-display text-3xl font-bold md:text-4xl">
                Convierte la teoría en fotografías que te hagan sentir orgulloso
              </h2>
              <p className="mx-auto mt-4 max-w-2xl leading-7 text-secondary-foreground/75">
                Continúa con una formación ordenada, ejercicios prácticos y explicaciones pensadas
                para avanzar desde los fundamentos.
              </p>
              <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
                <Link
                  to="/"
                  hash="cursos"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-cta px-7 py-3.5 font-semibold text-cta-foreground transition hover:brightness-95"
                >
                  Explorar cursos <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/"
                  hash="recursos"
                  className="inline-flex items-center justify-center rounded-full border border-white/30 px-7 py-3.5 font-semibold text-white transition hover:bg-white/10"
                >
                  Descargar los eBooks gratis
                </Link>
              </div>
            </section>
          </div>
        </article>
      </main>

      <footer className="bg-foreground text-background">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-12 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="FotoPasión" width={96} height={96} className="h-10 w-auto" />
            <span className="text-lg font-bold text-background">FotoPasión</span>
          </div>
          <div className="text-sm text-background/70">© {new Date().getFullYear()} FotoPasión</div>
        </div>
      </footer>
    </div>
  );
}
